import { useState } from "react";
import { Plus, Pencil, Ban, CheckCircle2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { adminApi, formatApiError } from "@/lib/adminAuth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { ErrorState } from "@/components/common/ErrorState";

const ROLES = [
  { value: "cliente", label: "Cliente (portal)" },
  { value: "operador", label: "Operador" },
  { value: "admin", label: "Administrador" },
];

export default function Usuarios() {
  usePageTitle("Usuarios");
  const queryClient = useQueryClient();
  const [dialogo, setDialogo] = useState(null); // { modo: 'nuevo'|'editar', usuario? }
  const [confirmar, setConfirmar] = useState(null); // usuario a activar/desactivar

  const { data: usuarios, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-usuarios"],
    queryFn: () => adminApi.get("/admin/usuarios").then((r) => r.data),
  });
  const { data: clientes = [] } = useQuery({
    queryKey: ["admin-clientes-opciones"],
    queryFn: () => adminApi.get("/admin/clientes/opciones").then((r) => r.data),
  });

  const activarMutation = useMutation({
    mutationFn: (usuario) => adminApi.put(`/admin/usuarios/${usuario.id}/activar`, null, { params: { activo: !usuario.activo } }),
    onSuccess: (_res, usuario) => {
      toast.success(usuario.activo ? "Usuario desactivado" : "Usuario activado");
      queryClient.invalidateQueries({ queryKey: ["admin-usuarios"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
    onSettled: () => setConfirmar(null),
  });

  return (
    <div className="mx-auto max-w-5xl px-8 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--tc-text)]">Usuarios</h1>
          <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">Equipo interno y accesos al portal de clientes. Solo visible para administradores.</p>
        </div>
        <button
          data-testid={ADMIN.usuariosNewButton}
          onClick={() => setDialogo({ modo: "nuevo" })}
          className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)]"
        >
          <Plus size={16} /> Nuevo usuario
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[var(--tc-accent)]" size={22} /></div>
        ) : isError ? (
          <ErrorState testId="admin-usuarios-error" mensaje="No se pudo cargar el listado de usuarios." onRetry={refetch} />
        ) : usuarios.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <Users size={26} className="text-[var(--tc-state-pending)]" />
            <p className="mt-3 text-sm text-[var(--tc-text-secondary)]">Todavía no hay usuarios.</p>
          </div>
        ) : (
          <table className="w-full min-w-[680px] text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <thead>
              <tr className="border-b border-[var(--tc-border)] text-left text-xs font-medium uppercase tracking-wide text-[var(--tc-text-secondary)]">
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Rol</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id} data-testid={ADMIN.usuariosRow} className="border-b border-[var(--tc-border)] last:border-0">
                  <td className="px-4 py-3 font-medium text-[var(--tc-text)]">{u.nombre}</td>
                  <td className="px-4 py-3 text-[var(--tc-text-secondary)]">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-[var(--tc-accent-soft)] px-2.5 py-1 text-xs font-medium uppercase text-[var(--tc-accent)]">{u.rol}</span>
                  </td>
                  <td className="px-4 py-3 text-[var(--tc-text-secondary)]">{u.cliente?.nombre || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${u.activo ? "bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]" : "bg-gray-100 text-gray-500"}`}>
                      {u.activo ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1.5">
                      <button data-testid={ADMIN.usuariosEditButton} onClick={() => setDialogo({ modo: "editar", usuario: u })} className="rounded-[var(--tc-radius)] p-1.5 text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)] hover:text-[var(--tc-accent)]">
                        <Pencil size={15} />
                      </button>
                      <button data-testid={ADMIN.usuariosToggleActivoButton} onClick={() => setConfirmar(u)} className="rounded-[var(--tc-radius)] p-1.5 text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)] hover:text-[var(--tc-accent)]">
                        {u.activo ? <Ban size={15} /> : <CheckCircle2 size={15} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dialogo && (
        <UsuarioDialog
          dialogo={dialogo}
          clientes={clientes}
          onClose={() => setDialogo(null)}
          onSaved={() => { setDialogo(null); queryClient.invalidateQueries({ queryKey: ["admin-usuarios"] }); }}
        />
      )}

      <AlertDialog open={Boolean(confirmar)} onOpenChange={(v) => !v && setConfirmar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmar?.activo ? "¿Desactivar usuario?" : "¿Activar usuario?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {`"${confirmar?.nombre}" quedará ${confirmar?.activo ? "sin acceso" : "con acceso"} de inmediato.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={ADMIN.confirmDialogCancel}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid={ADMIN.confirmDialogConfirm} onClick={() => activarMutation.mutate(confirmar)}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UsuarioDialog({ dialogo, clientes, onClose, onSaved }) {
  const usuario = dialogo.usuario;
  const [nombre, setNombre] = useState(usuario?.nombre || "");
  const [email, setEmail] = useState(usuario?.email || "");
  const [password, setPassword] = useState("");
  const [rol, setRol] = useState(usuario?.rol || "cliente");
  const [clienteId, setClienteId] = useState(usuario?.cliente_id || "");
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    const body = { nombre, email, rol, cliente_id: rol === "cliente" ? clienteId : null };
    if (password) body.password = password;
    try {
      if (dialogo.modo === "editar") {
        await adminApi.put(`/admin/usuarios/${usuario.id}`, body);
      } else {
        await adminApi.post("/admin/usuarios", body);
      }
      toast.success("Usuario guardado");
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogo.modo === "editar" ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Nombre</label>
            <input data-testid={ADMIN.usuarioFormNombre} required value={nombre} onChange={(e) => setNombre(e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Email</label>
            <input
              data-testid={ADMIN.usuarioFormEmail}
              type="email" required disabled={dialogo.modo === "editar"}
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)] disabled:bg-[var(--tc-surface)] disabled:text-[var(--tc-text-secondary)]"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">
              {dialogo.modo === "editar" ? "Nueva contraseña (opcional)" : "Contraseña inicial"}
            </label>
            <input
              data-testid={ADMIN.usuarioFormPassword}
              type="password" required={dialogo.modo === "nuevo"} minLength={6}
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Rol</label>
            <select data-testid={ADMIN.usuarioFormRol} value={rol} onChange={(e) => setRol(e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]">
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          {rol === "cliente" && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Cliente corporativo</label>
              <select data-testid={ADMIN.usuarioFormCliente} required value={clienteId} onChange={(e) => setClienteId(e.target.value)} className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]">
                <option value="">Seleccioná uno</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}
          {error && <p data-testid={ADMIN.usuarioFormError} className="rounded-[var(--tc-radius)] bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
          <DialogFooter>
            <button data-testid={ADMIN.usuarioFormSubmit} type="submit" disabled={guardando} className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
